import {
    Component,
    ElementRef,
    EventEmitter,
    Input,
    Output,
    QueryList,
    SimpleChanges,
    ViewChildren,
} from '@angular/core';
import { AbstractControl, UntypedFormArray, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { DocumentDto } from '@modeso/aumico-lib-cpmm-fe';
import { INameId } from '@modeso/types__aumico-lib-core-be';
import { I18NextService } from 'angular-i18next';
import moment, { Moment } from 'moment';
import { HamButtonComponent } from '../../../../../aumico-components/src/lib';
import { HamBadgeButtonComponent } from '../../../../../aumico-components/src/lib/components/badgeButton/badgeButton.component';
import { CreateAnnotationDto } from '../../dtos/create-annotation-dto.interface';
import { CreateTaskRequestDto } from '../../dtos/create-task-dto.interface';
import { UpdateAnnotationDto } from '../../dtos/update-annotation-dto.interface';
import { UpdateTaskDto } from '../../dtos/update-task-dto.interface';
import { TaskState } from '../../enums/task-state.enum';
import { WorkflowArea } from '../../enums/workflow-area.enum';
import { Annotation } from '../../interfaces/annotation.interface';
import { TaskMetaInformation } from '../../interfaces/task-meta-information.interface';
import { GetUserDto } from '@modeso/aumico-lib-users-fe';

interface EditableTask extends TaskMetaInformation {
    isEditing?: boolean;
    originalDescription?: string;
}

@Component({
    selector: 'pm-tasks-table',
    templateUrl: './pm-tasks-table.component.html',
    styleUrls: ['./pm-tasks-table.component.scss'],
})
export class PmTasksTableComponent {
    @Input() documentExpirationDate!: Date;
    @Input() tasks: EditableTask[] = [];
    @Input() assigneeOptions: INameId[] = [];
    @Input() annotationsMap: { [taskId: string]: Annotation[] } = {};
    @Input() documentsMap: { [taskId: string]: DocumentDto[] } = {};
    @Input() documentsInProgressUpload: { [taskId: string]: string[] } = {};
    @Input() currentUserId: string;
    @Input() sectionId: string;
    @Input() area: any;
    @Input() taskType: any;
    @Input() deletedUsers: GetUserDto[] = [];
    @Output() addTask = new EventEmitter<{ createTaskDto: CreateTaskRequestDto }>();
    @Output() updateTask = new EventEmitter<{ taskId: string; updateTaskDto: UpdateTaskDto }>();
    @Output() deleteTask = new EventEmitter<{ taskId: string }>();
    @Output() annotationAdded = new EventEmitter<{ taskId: string; annotation: CreateAnnotationDto }>();
    @Output() annotationChanged = new EventEmitter<{ annotationId: string; annotation: UpdateAnnotationDto }>();
    @Output() annotationRemoved = new EventEmitter<string>();
    @Output() documentRemoved = new EventEmitter<DocumentDto>();
    @Output() documentsAdded = new EventEmitter<DocumentDto[]>();
    @Output() documentsAddedInProgress = new EventEmitter<{ taskId: string; fileNames: string[] }>();
    @Output() openFile = new EventEmitter<string>();
    @Output() downloadFile = new EventEmitter<{ fileId: string; documentName: string }>();
    @Output() startPolling = new EventEmitter<string>();
    @Output() stopPolling = new EventEmitter<string>();

    @ViewChildren('taskInput') taskInputs: QueryList<ElementRef>;

    showDeleteModal: boolean = false;
    taskToDelete: TaskMetaInformation | null = null;
    statusOptions: any[] = [];
    tasksStatesFormArray: UntypedFormArray;

    // Enhanced Flyout properties
    openedStatusFlyoutTaskId: string | null = null;
    openedAssigneeFlyoutTaskId: string | null = null;
    openedDueDateFlyoutTaskId: string | null = null;
    openedOptionsMenuTaskId: string | null = null;
    flyoutPosition: {
        top: number;
        left: number;
        right: number;
        isAbove?: boolean;
    } = { top: 0, left: 0, right: 0, isAbove: false };

    // Flyout positioning constants
    private readonly DEFAULT_FLYOUT_HEIGHT = 200; // Default estimate
    private readonly DEFAULT_FLYOUT_WIDTH = 200; // Default estimate
    private readonly VIEWPORT_PADDING = 10; // Padding from viewport edges

    // --- New Task Row State ---
    newTaskRow: {
        sectionId: string | null;
        description: string;
        dueDate: Date;
        assignedTo: string | null;
        area: any;
        taskType: any;
        isActive: boolean;
    } = {
            sectionId: null,
            description: '',
            dueDate: null,
            assignedTo: null,
            area: null,
            taskType: null,
            isActive: false,
        };

    TaskState = TaskState;
    WorkflowArea = WorkflowArea;



    @Input() sectionToggled: boolean = false;
    @Input() selectedTaskIds: Set<string> = new Set<string>();
    @Output() toggleSelectAll = new EventEmitter<void>();
    @Output() toggleSelectTask = new EventEmitter<TaskMetaInformation>();

    // Range selection properties  
    private lastSelectedTaskIndex: number | null = null;
    calendarSelectedDate: Moment | null = null;

    constructor(private readonly i18NextService: I18NextService) {
        this.statusOptions = [
            { value: TaskState.OPEN, label: this.i18NextService.t('aumico_pm_tasks_status_open') as string },
            {
                value: TaskState.IN_PROGRESS,
                label: this.i18NextService.t('aumico_pm_tasks_status_in_progress') as string,
            },
            {
                value: TaskState.IN_REVIEW,
                label: this.i18NextService.t('aumico_pm_tasks_status_in_review') as string,
            },
            { value: TaskState.DONE, label: this.i18NextService.t('aumico_pm_tasks_status_done') as string },
        ];
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['tasks'] && this.tasks?.length) {
            this.tasksStatesFormArray = this.getAllTasksStatesAsFormArray();
            // Reset range selection when tasks change
            this.lastSelectedTaskIndex = null;
        }
    }

    private getAllTasksStatesAsFormArray(): UntypedFormArray {
        const taskArray = this.tasks.map((task) => {
            return {
                taskId: task.id,
                taskState: task.taskState,
                taskDueDate: task.dueDate,
            };
        });
        const formGroups: AbstractControl[] = taskArray.map((entry) => this.addFormGroup(entry));
        return new UntypedFormArray(formGroups);
    }

    private addFormGroup(taskState): UntypedFormGroup {
        const dataSourceFormGroup = {};
        Object.keys(taskState).forEach((key) => {
            dataSourceFormGroup[key] = new UntypedFormControl(taskState[key]);
        });
        return new UntypedFormGroup(dataSourceFormGroup);
    }

    /**
     * Calculates the optimal position for a flyout to ensure it's fully visible
     * @param buttonRect The button's bounding rectangle
     * @param flyoutElement Optional flyout element to get actual dimensions
     * @param flyoutWidth Fallback width if element not available
     * @param flyoutHeight Fallback height if element not available
     * @returns Position object with top, left, right values and isAbove flag
     */
    private calculateFlyoutPosition(
        buttonRect: DOMRect,
        flyoutElement?: HTMLElement,
        flyoutWidth: number = this.DEFAULT_FLYOUT_WIDTH,
        flyoutHeight: number = this.DEFAULT_FLYOUT_HEIGHT,
    ): { top: number; left: number; right: number; isAbove: boolean } {
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        // Use actual flyout dimensions if available, otherwise use defaults
        const actualWidth = flyoutElement ? flyoutElement.offsetWidth : flyoutWidth;
        const actualHeight = flyoutElement ? flyoutElement.offsetHeight : flyoutHeight;

        // Calculate if flyout fits below the button
        const spaceBelow = viewportHeight - buttonRect.bottom;
        const spaceAbove = buttonRect.top;
        const fitsBelow = spaceBelow >= actualHeight + this.VIEWPORT_PADDING;
        const fitsAbove = spaceAbove >= actualHeight + this.VIEWPORT_PADDING;

        // Determine vertical position
        let top: number;
        let isAbove = false;

        if (fitsBelow) {
            // Position below button
            top = buttonRect.bottom;
        } else if (fitsAbove) {
            // Position above button
            top = buttonRect.top - actualHeight;
            isAbove = true;
        } else {
            // If it doesn't fit either way, position where there's more space
            if (spaceBelow > spaceAbove) {
                top = buttonRect.bottom;
            } else {
                top = buttonRect.top - actualHeight;
                isAbove = true;
            }
        }

        // Calculate horizontal position
        let left = buttonRect.left;
        let right = buttonRect.right;

        // Check if flyout extends beyond right edge of viewport
        if (buttonRect.left + actualWidth > viewportWidth - this.VIEWPORT_PADDING) {
            // Align flyout's right edge with button's right edge
            left = buttonRect.right - actualWidth;
            right = buttonRect.right;
        }

        // Ensure flyout doesn't extend beyond left edge
        if (left < this.VIEWPORT_PADDING) {
            left = this.VIEWPORT_PADDING;
            right = left + actualWidth;
        }

        return { top, left, right, isAbove };
    }

    /**
     * Repositions a flyout after it's rendered using its actual dimensions
     * @param flyoutSelector CSS selector for the flyout element
     * @param buttonRect The button's bounding rectangle
     */
    private repositionFlyoutAfterRender(flyoutSelector: string, buttonRect: DOMRect): void {
        setTimeout(() => {
            const flyoutElement = document.querySelector(flyoutSelector) as HTMLElement;
            if (flyoutElement) {
                const position = this.calculateFlyoutPosition(buttonRect, flyoutElement);
                this.flyoutPosition = {
                    top: position.top,
                    left: position.left,
                    right: position.right,
                    isAbove: position.isAbove,
                };
            }
        }, 0);
    }

    // EDITING ASSIGNEE FUNCTIONS

    getInitials(name: string): string {
        if (!name) return '';
        if (name === this.i18NextService.t('aumico_tasks_unassigned')) {
            return '-';
        }
        const parts = name.split(' ');
        return parts
            .map((p) => p[0])
            .join('')
            .toUpperCase();
    }

    openAssigneeFlyout(taskId: string, buttonComponent: HamButtonComponent): void {
        const buttonElement = buttonComponent.nativeButton?.nativeElement;

        if (!buttonElement) return;

        const rect = buttonElement.getBoundingClientRect();
        const position = this.calculateFlyoutPosition(rect, undefined, 224, 280); // Assignee flyouts are often larger

        this.flyoutPosition = {
            top: position.top,
            right: position.right,
            left: position.left,
            isAbove: position.isAbove,
        };

        this.openedStatusFlyoutTaskId = null;
        this.openedAssigneeFlyoutTaskId = taskId;

        // Reposition after render with actual dimensions
        this.repositionFlyoutAfterRender('.assignee-flyout', rect);
    }

    closeAssigneeFlyout(): void {
        this.openedAssigneeFlyoutTaskId = null;
    }

    updateAssignee(task: TaskMetaInformation, newAssignee: string | null): void {
        this.updateTask.emit({ taskId: task.id, updateTaskDto: { assignedTo: newAssignee } });
        this.closeAssigneeFlyout();
    }

    getAssigneeName(id: string | null): string {
        if (!id) return this.i18NextService.t('aumico_tasks_unassigned') as string;
        
        // First check active users
        const foundActive = this.assigneeOptions.find((opt) => opt.id === id);
        if (foundActive) return foundActive.name;
        
        // Then check deleted users
        const foundDeleted = this.deletedUsers.find((user) => user.id === id);
        if (foundDeleted) return `${foundDeleted.firstName} ${foundDeleted.lastName}`;
        
        // If not found anywhere, return the ID
        return id;
    }

    isUserDeleted(id: string | null): boolean {
        if (!id) return false;
        
        // Check if user exists in deletedUsers array
        return this.deletedUsers.some((user) => user.id === id);
    }

    /**
     * Sorts assignee options to show the current assignee (or "Unassigned") first.
     * @param task The task for which to sort the options.
     * @returns A new array of sorted assignee options.
     */
    getSortedAssigneeOptions(task: EditableTask): INameId[] {
        if (!this.assigneeOptions || this.assigneeOptions.length === 0) {
            return [];
        }

        const sortedOptions = [...this.assigneeOptions];
        const assignedUserId = task.assignedTo;

        // Find the index of the currently assigned user or the "Unassigned" option
        const targetIndex = sortedOptions.findIndex((opt) => opt.id === assignedUserId);

        // If the target is found and it's not already the first item, move it to the top
        if (targetIndex > 0) {
            const [targetOption] = sortedOptions.splice(targetIndex, 1);
            sortedOptions.unshift(targetOption);
        }

        return sortedOptions;
    }

    // EDITING DUE DATE FUNCTIONS

    openDueDateFlyout(taskId: string, buttonComponent: HamButtonComponent): void {
        const buttonElement = buttonComponent.nativeButton?.nativeElement;

        if (!buttonElement) return;

        const rect = buttonElement.getBoundingClientRect();
        const position = this.calculateFlyoutPosition(rect, undefined, 224, 446); // Due date flyouts are often larger

        this.flyoutPosition = {
            top: position.top,
            right: position.right,
            left: position.left,
            isAbove: position.isAbove,
        };

        this.openedStatusFlyoutTaskId = null;
        this.openedAssigneeFlyoutTaskId = null;
        this.openedDueDateFlyoutTaskId = taskId;

        // Reposition after render with actual dimensions
        this.repositionFlyoutAfterRender('.due-date-flyout', rect);

        // Set calendarSelectedDate to the task's due date
        const task = this.tasks.find(item => item.id === taskId);
        const taskDueDate = task?.dueDate ? new Date(task.dueDate) : null;
        const normalizedDueDate = taskDueDate
            ? moment.utc(taskDueDate).startOf('day')
            : null;
        this.calendarSelectedDate = normalizedDueDate;
    }

    closeDueDateFlyout(): void {
        this.openedDueDateFlyoutTaskId = null;
        this.calendarSelectedDate = null;
    }

    selectToday(task: TaskMetaInformation): void {
        const today = new Date();
        // Strip time to ensure consistency and visual match in mat-calendar
        const normalized = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0, 0));
        this.calendarSelectedDate = moment.utc(normalized).startOf('day');
        this.updateTask.emit({ taskId: task.id, updateTaskDto: { dueDate: normalized } });
        this.closeDueDateFlyout();
    }

    clearDate(task: TaskMetaInformation): void { }

    updateDueDate(task: TaskMetaInformation, newDueDate: Moment): void {
        if (!newDueDate) return;

        if (newDueDate) {
            // Build UTC midnight for that calendar day
            const year = newDueDate.year();
            const month = newDueDate.month(); // 0-based
            const dday = newDueDate.date();

            const asUtc = new Date(Date.UTC(year, month, dday, 0, 0, 0, 0));
            this.updateTask.emit({ taskId: task.id, updateTaskDto: { dueDate: asUtc } });
            this.closeDueDateFlyout();
        }
    }

    isOverdue(dueDate: string | Date): boolean {
        return new Date(dueDate) < new Date();
    }

    isDueSoon(dueDate: string | Date): boolean {
        const today = new Date();
        const due = new Date(dueDate);

        const timeDiff = due.getTime() - today.getTime();
        const daysDiff = timeDiff / (1000 * 60 * 60 * 24);

        return daysDiff >= 0 && daysDiff <= 7;
    }

    // EDITING TASK STATE FUNCTIONS

    getStatusLabel(status: TaskState): string {
        return this.statusOptions.find((opt) => opt.value === status)?.label ?? 'Select';
    }

    openStatusFlyout(taskId: string, buttonComponent: HamBadgeButtonComponent): void {
        const buttonElement = buttonComponent.nativeButton?.nativeElement;

        if (!buttonElement) return;

        const rect = buttonElement.getBoundingClientRect();
        const position = this.calculateFlyoutPosition(rect, undefined, 224, 148); // Status flyouts are often smaller

        this.flyoutPosition = {
            top: position.top,
            right: position.right,
            left: position.left,
            isAbove: position.isAbove,
        };

        this.openedAssigneeFlyoutTaskId = null;
        this.openedStatusFlyoutTaskId = taskId;

        // Reposition after render with actual dimensions
        this.repositionFlyoutAfterRender('.status-flyout', rect);
    }

    closeStatusFlyout(): void {
        this.openedStatusFlyoutTaskId = null;
    }

    updateStatus(task: TaskMetaInformation, newStatus: TaskState): void {
        this.updateTask.emit({ taskId: task.id, updateTaskDto: { taskState: newStatus } });
        this.closeStatusFlyout();
    }

    onClosingTaskStatusChange(task: EditableTask, isChecked: boolean): void {
        const newStatus = isChecked ? TaskState.DONE : TaskState.OPEN;
        this.updateTask.emit({ taskId: task.id, updateTaskDto: { taskState: newStatus } });
    }

    // Options menu for 3-dots button
    toggleOptionsMenu(event: MouseEvent, taskId: string, buttonElementRef?: ElementRef): void {
        event.stopPropagation();

        if (buttonElementRef) {
            const buttonElement = (buttonElementRef as any).nativeElement || buttonElementRef;
            const rect = buttonElement.getBoundingClientRect();
            const position = this.calculateFlyoutPosition(rect, undefined, 222, 134); // Options menu dimensions

            this.flyoutPosition = {
                top: position.top,
                left: position.left,
                right: position.right,
                isAbove: position.isAbove,
            };
        }

        if (this.openedOptionsMenuTaskId === taskId) {
            this.openedOptionsMenuTaskId = null;
        } else {
            this.openedOptionsMenuTaskId = taskId;

            // Reposition after render with actual dimensions if buttonElementRef is available
            if (buttonElementRef) {
                const buttonElement = (buttonElementRef as any).nativeElement || buttonElementRef;
                const rect = buttonElement.getBoundingClientRect();
                this.repositionFlyoutAfterRender('.options-menu-flyout', rect);
            }
        }

        // Close other flyouts
        this.openedStatusFlyoutTaskId = null;
        this.openedAssigneeFlyoutTaskId = null;
        this.openedDueDateFlyoutTaskId = null;
    }

    closeOptionsMenu(): void {
        this.openedOptionsMenuTaskId = null;
    }

    startEditing(task: EditableTask): void {
        this.closeOptionsMenu(); // Close options menu if open
        this.tasks.forEach((t) => (t.isEditing = false)); // Close other editing tasks
        task.originalDescription = task.description;
        task.isEditing = true;

        setTimeout(() => {
            const editingTask = this.tasks.find((t) => t.isEditing);
            if (editingTask) {
                const editingIndex = this.tasks.filter((t) => t.isEditing).indexOf(editingTask);
                const inputElement = this.taskInputs.toArray()[editingIndex];
                if (inputElement) {
                    inputElement.nativeElement.focus();
                }
            }
        });
    }

    finishEditing(task: EditableTask, event: any): void {
        const newDescription = event.target.value;
        if (newDescription && newDescription !== task.originalDescription) {
            this.updateTask.emit({
                taskId: task.id,
                updateTaskDto: { description: newDescription },
            });
        }
        task.isEditing = false;
    }

    cancelEditing(task: EditableTask): void {
        task.isEditing = false;
        task.description = task.originalDescription;
    }

    onMarkTaskNotApplicable(taskId: string): void {
        this.updateTask.emit({ taskId: taskId, updateTaskDto: { taskState: TaskState.NOT_APPLICABLE } });
        this.closeOptionsMenu();
    }

    onMarkTaskApplicable(taskId: string): void {
        this.updateTask.emit({ taskId: taskId, updateTaskDto: { taskState: TaskState.OPEN } });
        this.closeOptionsMenu();
    }

    onDeleteTask(task: TaskMetaInformation): void {
        this.showDeleteModal = true;
        this.taskToDelete = task;
        this.closeOptionsMenu();
    }

    confirmDeleteTask(): void {
        this.deleteTask.emit({ taskId: this.taskToDelete.id });
        this.showDeleteModal = false;
        this.taskToDelete = null;
    }

    cancelDeleteTask(): void {
        this.showDeleteModal = false;
        this.taskToDelete = null;
    }

    getTruncatedContent(content: string | undefined, maxLength: number = 50): string {
        if (!content) return '';
        return content.length > maxLength ? content.slice(0, maxLength) + '...' : content;
    }

    onAddTask(taskId: string): void {
        //this.addTask.emit({ taskId: taskId });
        //this.closeOptionsMenu();
    }

    // ANNOTATIONS FUNCTIONS

    onAnnotationAdded(
        taskId: string,
        annotation: { content: string; mentions?: string[]; taskName?: string; workflowArea?: string },
    ) {
        this.annotationAdded.emit({
            taskId,
            annotation: {
                content: annotation.content,
                mentions: annotation.mentions,
                taskName: annotation.taskName ?? '',
                workflowArea: annotation.workflowArea,
            },
        });
    }

    onAnnotationChanged(
        annotationId: string,
        annotation: { content: string; mentions?: string[]; taskName?: string; workflowArea?: string },
    ) {
        this.annotationChanged.emit({
            annotationId,
            annotation: {
                content: annotation.content,
                mentions: annotation.mentions,
                taskName: annotation.taskName ?? '',
                workflowArea: annotation.workflowArea,
            },
        });
    }

    onAnnotationRemoved(annotationId: string) {
        this.annotationRemoved.emit(annotationId);
    }

    public onDocumentRemoved(document: DocumentDto): void {
        this.documentRemoved.emit(document);
    }

    public onDocumentsAdded(documents: DocumentDto[]): void {
        this.documentsAdded.emit(documents);
    }

    public onDocumentsAddedInProgress(taskId: string, fileNames: string[]): void {
        this.documentsAddedInProgress.emit({ taskId, fileNames });
    }

    getTaskStateClass(state: number): string {
        // Returns the enum key as a string, e.g. 'NOT_APPLICABLE', 'OPEN', etc.
        return TaskState[state];
    }

    // onNewTask
    onNewTask(sectionId?: string, area?: any, taskType?: any): void {
        // Always require a taskType
        const resolvedTaskType = taskType || this.taskType;
        if (!resolvedTaskType) {
            alert('Task type must be set to create a new task.');
            return;
        }
        this.newTaskRow = {
            sectionId: sectionId || this.sectionId || null,
            description: '',
            dueDate: this.getDefaultDueDate(),
            assignedTo: this.currentUserId,
            area: area || this.area || null,
            taskType: resolvedTaskType,
            isActive: true,
        };
        setTimeout(() => {
            const input = document.getElementById('new-task-desc-input');
            if (input) (input as HTMLInputElement).focus();
        }, 0);
    }

    getDefaultDueDate(): Date {
        const today = new Date();
        today.setDate(today.getDate() + 7);
        return today;
    }

    onNewTaskDescriptionBlurOrEnter(event: any, sectionId: string): void {
        const desc = this.newTaskRow.description.trim();
        if (desc) {
            const { sectionId, description, dueDate, assignedTo, area, taskType } = this.newTaskRow;
            const resolvedSectionId = sectionId || this.sectionId;
            const createTaskDto = {
                sectionId: resolvedSectionId,
                description: desc,
                dueDate: area === WorkflowArea.MANDATE_MANAGEMENT ? dueDate : null,
                assignedTo: area === WorkflowArea.MANDATE_MANAGEMENT ? assignedTo : null,
                area,
                taskType,
            };

            this.addTask.emit({ createTaskDto });
        }
        this.newTaskRow = {
            sectionId: null,
            description: '',
            dueDate: null,
            assignedTo: null,
            area: null,
            taskType: null,
            isActive: false,
        };
    }

    onOpenFile(fileId: string): void {
        this.openFile.emit(fileId);
    }

    onDownloadFile(fileId: string, documentName: string): void {
        this.downloadFile.emit({ fileId, documentName });
    }

    onStartPolling(fileId: string): void {
        this.startPolling.emit(fileId);
    }

    onStopPolling(fileId: string): void {
        this.stopPolling.emit(fileId);
    }
    toggleCheckAll() {
        // Just emit event, let parent handle the logic
        this.toggleSelectAll.emit();
    }

    toggleCheckTask(task: TaskMetaInformation, event?: MouseEvent) {
        // Handle shift+click for range selection
        if (event?.shiftKey && this.lastSelectedTaskIndex !== null) {
            this.handleShiftClick(task);
        } else {
            this.handleNormalClick(task);
        }
    }

    onTaskCheckboxClick(task: TaskMetaInformation, event: MouseEvent): void {
        // Stop propagation to prevent row click
        event.stopPropagation();

        // Handle shift+click for range selection
        if (event.shiftKey && this.lastSelectedTaskIndex !== null) {
            this.handleShiftClick(task);
        } else {
            this.handleNormalClick(task);
        }
    }

    private handleShiftClick(clickedTask: TaskMetaInformation): void {
        const clickedIndex = this.tasks.findIndex(task => task.id === clickedTask.id);

        if (clickedIndex === -1 || this.lastSelectedTaskIndex === null) {
            this.handleNormalClick(clickedTask);
            return;
        }

        // Determine range
        const startIndex = Math.min(this.lastSelectedTaskIndex, clickedIndex);
        const endIndex = Math.max(this.lastSelectedTaskIndex, clickedIndex);

        // Update last selected
        this.lastSelectedTaskIndex = clickedIndex;

        // Get all tasks in the range
        const tasksInRange = this.tasks.slice(startIndex, endIndex + 1);

        // Check if the clicked task is currently selected
        const isClickedTaskSelected = this.selectedTaskIds.has(clickedTask.id);

        // Filter tasks based on the clicked task's selection state
        const tasksToToggle = tasksInRange.filter(task => {
            const isTaskSelected = this.selectedTaskIds.has(task.id);

            if (isClickedTaskSelected) {
                // Clicked task is selected → only toggle currently selected tasks (to unselect them)
                return isTaskSelected;
            } else {
                // Clicked task is unselected → only toggle currently unselected tasks (to select them)
                return !isTaskSelected;
            }
        });

        // Emit each task selection to maintain existing event structure
        tasksToToggle.forEach(task => {
            this.toggleSelectTask.emit(task);
        });
    }

    private handleNormalClick(task: TaskMetaInformation): void {
        // Update last selected position for non-shift clicks
        this.lastSelectedTaskIndex = this.tasks.findIndex(t => t.id === task.id);

        // Just emit event, let parent handle the logic (existing behavior)
        this.toggleSelectTask.emit(task);
    }


    getDocumentsForTask(task: TaskMetaInformation): DocumentDto[] {
        const taskDocs = this.documentsMap[task['id']] || [];
        const permanentDocs = this.documentsMap[task['staticTaskReference']] || [];
        const allDocs = [...taskDocs, ...permanentDocs];

        // Filter out duplicates by id
        const uniqueDocs = allDocs.filter((doc, index, self) => index === self.findIndex((d) => d.id === doc.id));

        return uniqueDocs;
    }
}
